import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, HostListener } from '@angular/core';
import { Subscription, pairwise, startWith } from 'rxjs';
import { AiChatStateService, ChatMessage, ChatSession, SessionGroup } from '../ai-chat-state.service';
import { AuthServiceService } from '../../Auth/auth-service.service';
import { ScreenCaptureService } from '../screen-capture.service';
import { ServiceService } from '../../service.service';
import { VoiceService } from '../voice.service';

interface SuggestCard {
  icon: 'chart' | 'users' | 'inventory' | 'bulb';
  title: string;
  sub: string;
  ask: string;
}

@Component({
  selector: 'app-ai-chat-page',
  templateUrl: './ai-chat-page.component.html',
  styleUrls: ['./ai-chat-page.component.css']
})
export class AiChatPageComponent implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild('threadEl') threadEl?: ElementRef<HTMLDivElement>;
  @ViewChild('composerEl') composerEl?: ElementRef<HTMLTextAreaElement>;

  // ── Local UI state ─────────────────────────────────────────────
  collapsed = false;
  composerFocused = false;
  inputText = '';
  searchQuery = '';
  /** Mobile drawer state — the sidebar slides in over the thread on small screens. */
  mobileSidebarOpen = false;

  // F.2: saved / favorite queries the user can tap to re-run.
  favorites: { id: string; label: string; query_text: string }[] = [];
  // F.1: server-side content-search hits (sessions whose MESSAGES match, not just titles).
  contentMatches: { session_id: string; title: string; snippet: string }[] = [];
  private searchDebounce: any = null;

  // ── Auth ───────────────────────────────────────────────────────
  // Live getters (not one-time captures) — see ai-assistant.component.ts note:
  // caching these at init froze pre-login values on fresh browser sessions.
  get userType(): string { return localStorage.getItem('userType') || ''; }
  get employeeName(): string { return localStorage.getItem('EmployeeName') || 'User'; }
  isLoggedIn = true;

  // ── State mirrored from service (kept here for template ease) ──
  sessionGroups: SessionGroup[] = [];
  filteredGroups: SessionGroup[] = [];
  messages: ChatMessage[] = [];
  currentSessionId: string | null = null;
  currentTitle = 'New chat';
  isLoading = false;
  isStreaming = false;
  suggestions: string[] = [];
  elapsedSecs = 0;

  /** Copy-answer feedback + jump-to-latest state (parity with the widget). */
  copiedIdx: number | null = null;
  atBottom = true;
  showJumpBtn = false;

  /** Chip strip expand toggle — collapsed by default; resets on each new suggestions emission */
  chipsExpanded = false;
  /** Default number of chips to show when the strip is collapsed */
  readonly CHIP_VISIBLE_LIMIT = 2;

  // ── Report state ───────────────────────────────────────────────
  isReporting = false;
  reportToast: { type: 'success' | 'error'; text: string } | null = null;
  reportModalOpen = false;
  reportText = '';
  /** Can only report once there's a real conversation (at least one user message). */
  get canReport(): boolean { return this.messages.some(m => m.role === 'user'); }

  // ── Voice state ────────────────────────────────────────────────
  isListening = false;
  isSpeaking = false;
  /** Which message index is currently being spoken (null if none) */
  currentSpeakingIndex: number | null = null;
  /** Tracks whether the active listening session is the "product lookup" variant */
  productVoiceActive = false;
  /** Live interim text shown inside the voice popup while the user is speaking. */
  liveTranscript = '';
  /** OCR: when a photo is staged but not yet sent, holds the full data URL
   *  (so the preview img can render it; backend gets the base64 portion at send time). */
  pendingPhotoDataUrl: string | null = null;
  pendingPhotoName: string | null = null;

  /** Uploaded document (PDF/Word/Excel): extracted + company-relevance-gated
   *  server-side. pendingDocId rides along with the next message. */
  pendingDocId: string | null = null;
  pendingDocName: string | null = null;
  docTruncated = false;
  docBusy = false;
  docError: string | null = null;

  get voiceSupported(): boolean { return this.voice.supported.recognition; }
  get speechSupported(): boolean { return this.voice.supported.synthesis; }

  /** OCR: file/camera input → store data URL + stash + auto-prompt if input empty. */
  onPhotoAttached(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.pendingPhotoDataUrl = reader.result as string;
      this.pendingPhotoName = file.name || 'photo';
      if (!this.inputText.trim()) {
        this.inputText = 'Extract this invoice: vendor, date, line items, totals.';
        setTimeout(() => this.autoResizeRef(), 0);
      }
    };
    reader.readAsDataURL(file);
    input.value = '';  // allow re-attaching the same file
  }

  /** Discard the currently-staged photo (X button in the preview). */
  removePendingPhoto(): void {
    this.pendingPhotoDataUrl = null;
    this.pendingPhotoName = null;
  }

  /** Document attach (PDF / Word / Excel) → upload for server-side extraction +
   *  the company-relevance gate. Accepted → stage docId; rejected → show why. */
  async onDocAttached(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    input.value = '';
    this.removePendingPhoto();          // one attachment at a time
    this.removePendingDoc();
    this.pendingDocName = file.name || 'document';
    this.docBusy = true;
    try {
      const res = await this.state.uploadDocument(file, this.userType, this.employeeName);
      if (res && res.ok) {
        this.pendingDocId = res.docId;
        this.pendingDocName = res.filename || file.name;
        this.docTruncated = !!res.truncated;
        if (!this.inputText.trim()) {
          this.inputText = 'Summarise this document and pull out the key figures.';
          setTimeout(() => this.autoResizeRef(), 0);
        }
      } else {
        this.pendingDocName = null;
        this.docError = (res && res.message) || "I couldn't read that file.";
      }
    } catch {
      this.pendingDocName = null;
      this.docError = 'Upload failed — please check your connection and try again.';
    } finally {
      this.docBusy = false;
    }
  }

  /** Discard the staged document / clear any rejection notice. */
  removePendingDoc(): void {
    this.pendingDocId = null;
    this.pendingDocName = null;
    this.docError = null;
    this.docTruncated = false;
  }

  // Empty-state suggestions — wired to common ERP questions.
  emptySuggestions: SuggestCard[] = [
    { icon: 'chart', title: 'Sales overview', sub: 'This month vs last', ask: 'Give me a sales overview for this month compared to last month' },
    { icon: 'users', title: 'Top customers', sub: 'Revenue ranking', ask: 'Show me the top 10 customers by revenue this year' },
    { icon: 'inventory', title: 'Low stock alerts', sub: 'Products needing reorder', ask: 'Which products are running low on stock and need reordering?' },
    { icon: 'bulb', title: 'Salesperson targets', sub: 'Target vs actual performance', ask: 'Show me each salesperson\'s target vs actual for this month' },
  ];

  private shouldScroll = false;
  private subs: Subscription[] = [];

  constructor(
    public state: AiChatStateService,
    private auth: AuthServiceService,
    private screenCapture: ScreenCaptureService,
    private service: ServiceService,
    private voice: VoiceService
  ) { }

  // ── Lifecycle ──────────────────────────────────────────────────
  ngOnInit(): void {
    // userType / employeeName are live getters now — no one-time capture here.

    this.subs.push(this.auth.isAuthenticated.subscribe(v => this.isLoggedIn = v));
    this.loadFavorites();   // F.2

    this.subs.push(this.state.sessionGroups$.subscribe(g => {
      this.sessionGroups = g;
      this.applySearchFilter();
    }));
    this.subs.push(this.state.messages$.subscribe(m => {
      this.messages = m;
      this.shouldScroll = true;
    }));
    this.subs.push(this.state.currentSessionId$.subscribe(id => this.currentSessionId = id));
    this.subs.push(this.state.currentTitle$.subscribe(t => this.currentTitle = t));
    this.subs.push(this.state.isLoading$.subscribe(v => this.isLoading = v));
    this.subs.push(this.state.isStreaming$.subscribe(v => this.isStreaming = v));
    // Scroll-on-suggestions: when follow-up chips become visible the layout
    // shifts; force a re-scroll so the latest content stays in view.
    this.subs.push(this.state.suggestions$.subscribe(s => {
      this.suggestions = s;
      // Each new AI response starts with a collapsed chip strip
      this.chipsExpanded = false;
      if (s && s.length > 0) this.shouldScroll = true;
    }));
    // Scroll-on-finalize: pin to bottom when streaming transitions true → false.
    this.subs.push(this.state.isStreaming$.pipe(
      startWith(false), pairwise()
    ).subscribe(([prev, curr]) => {
      if (prev === true && curr === false) this.shouldScroll = true;
    }));
    this.subs.push(this.state.elapsedSecs$.subscribe(s => this.elapsedSecs = s));

    // Voice state mirroring (for CSS class bindings)
    this.subs.push(this.voice.isListening$.subscribe(v => {
      this.isListening = v;
      if (!v) {
        this.liveTranscript = '';
        if (!this.isListening) this.productVoiceActive = false;
      }
    }));
    this.subs.push(this.voice.isSpeaking$.subscribe(v => {
      this.isSpeaking = v;
      if (!v) this.currentSpeakingIndex = null;
    }));
    this.subs.push(this.voice.transcript$.subscribe(t => this.liveTranscript = t || ''));

    this.state.loadSessions(this.employeeName);

    // If the service has no active conversation, show the welcome inline.
    if (this.state.messages.length === 0) {
      this.state.showWelcome(this.employeeName);
    }

    // v2.9 (C.1): mobile soft-keyboard handling. interactive-widget=resizes-content
    // in the viewport meta already does most of the work on Chrome Android, but
    // iOS Safari + older Android need the visualViewport fallback to keep the
    // composer above the keyboard. Sets --kbd-offset on the page root.
    this.setupKeyboardHandler();
  }

  // ── Soft-keyboard handler (C.1) ──────────────────────────────────────────
  private _vvHandler: ((this: VisualViewport, ev: Event) => any) | null = null;
  private setupKeyboardHandler(): void {
    if (typeof window === 'undefined') return;
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    if (!vv) return;  // unsupported (older Safari/Firefox) — interactive-widget meta handles it
    const onResize = () => {
      // Diff between layout viewport and visual viewport = keyboard height.
      // window.innerHeight is the layout viewport; vv.height is what's visible.
      const kbd = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      document.documentElement.style.setProperty('--kbd-offset', `${kbd}px`);
      document.documentElement.classList.toggle('kbd-open', kbd > 50);
      // Keep the latest message visible when the keyboard opens.
      if (kbd > 50) this.shouldScroll = true;
    };
    this._vvHandler = onResize;
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    onResize();
  }
  private teardownKeyboardHandler(): void {
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    if (vv && this._vvHandler) {
      vv.removeEventListener('resize', this._vvHandler);
      vv.removeEventListener('scroll', this._vvHandler);
      this._vvHandler = null;
    }
    document.documentElement.style.removeProperty('--kbd-offset');
    document.documentElement.classList.remove('kbd-open');
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.teardownKeyboardHandler();
    try { this.voice.stopListening(); } catch { }
    try { this.voice.stopSpeaking(); } catch { }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.shouldScroll = false;
      this.scrollToBottom();
    }
  }

  // ── Computed ───────────────────────────────────────────────────
  get showEmpty(): boolean {
    // Only show empty state when no real conversation is in progress —
    // i.e. exactly the welcome message and the user hasn't typed yet.
    return this.messages.length <= 1 && !this.isLoading && !this.isStreaming && !this.currentSessionId;
  }

  get initials(): string {
    const n = (this.employeeName || 'U').trim();
    const parts = n.split(/\s+/).slice(0, 2);
    return parts.map(p => p[0]?.toUpperCase() || '').join('') || 'U';
  }

  get greeting(): string {
    const h = new Date().getHours();
    const name = (this.employeeName || '').split(/\s+/)[0] || 'there';
    if (h < 5) return `Burning the midnight oil, ${name}`;
    if (h < 12) return `Good morning, ${name}`;
    if (h < 17) return `Good afternoon, ${name}`;
    return `Good evening, ${name}`;
  }

  // ── Actions ────────────────────────────────────────────────────
  newChat(): void {
    this.mobileSidebarOpen = false;
    this.state.newChat(this.employeeName);
    this.inputText = '';
    setTimeout(() => this.composerEl?.nativeElement.focus(), 60);
  }

  openSession(s: ChatSession): void {
    this.mobileSidebarOpen = false;
    this.atBottom = true;
    this.state.openSession(s, this.employeeName);
  }

  deleteSession(s: ChatSession, e: Event): void {
    e.stopPropagation();
    if (!confirm('Delete this conversation? This cannot be undone.')) return;
    this.state.deleteSession(s.session_id, this.employeeName);
  }

  send(): void {
    if (this.docBusy) return;   // wait for an in-flight document upload to finish
    const text = this.inputText.trim();
    if (!text) return;
    this.atBottom = true;       // a new turn always follows to the bottom
    this.inputText = '';
    // Extract raw base64 (strip "data:image/jpeg;base64," prefix) for the backend.
    const dataUrl = this.pendingPhotoDataUrl;
    this.pendingPhotoDataUrl = null;
    this.pendingPhotoName = null;
    const comma = dataUrl ? dataUrl.indexOf(',') : -1;
    const base64 = (dataUrl && comma >= 0) ? dataUrl.slice(comma + 1) : null;
    const docId = this.pendingDocId;
    this.removePendingDoc();
    this.state.sendMessage(text, this.userType, this.employeeName, base64, docId);
    setTimeout(() => this.autoResizeRef(), 0);
  }

  /** v2.9 (C.2): retry a user message whose delivery='failed'. */
  retryFailed(idx: number): void {
    this.state.retryFailedMessage(idx, this.userType, this.employeeName);
  }

  /** Regenerate the latest answer — re-asks the same question for a fresh reply. */
  regenerate(): void {
    if (this.isStreaming || this.isLoading) return;
    this.atBottom = true;
    this.state.regenerateLast(this.userType, this.employeeName);
  }

  /** Copy an answer to the clipboard as clean plain text. */
  async copyMsg(index: number, text: string): Promise<void> {
    const ok = await this.state.copyMessageText(text);
    if (ok) { this.copiedIdx = index; setTimeout(() => { if (this.copiedIdx === index) this.copiedIdx = null; }, 1600); }
  }

  /** Sidebar: rename a chat (simple prompt — fine for an internal tool). */
  renameChat(s: ChatSession, ev?: Event): void {
    ev?.stopPropagation();
    const name = window.prompt('Rename this chat:', s.title || '');
    if (name && name.trim()) this.state.renameSession(s.session_id, name.trim(), this.employeeName);
  }

  /** Track whether the user is near the bottom (so streaming doesn't yank them up). */
  onThreadScroll(): void {
    const el = this.threadEl?.nativeElement;
    if (!el) return;
    const nearBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) < 80;
    this.atBottom = nearBottom;
    if (nearBottom) this.showJumpBtn = false;
  }

  /** Jump-to-latest pill → snap to the newest message and resume following. */
  jumpToLatest(): void {
    this.atBottom = true;
    this.showJumpBtn = false;
    this.scrollToBottom();
  }

  // ── v2.9 (C.4): pull-to-refresh on the sidebar sessions list ────────────
  // Native mobile pattern — drag down past the top of the list to reload sessions.
  ptrStartY = 0;
  ptrPullDist = 0;
  ptrRefreshing = false;
  ptrEnabled = false;  // only arms when the list is scrolled to the very top

  onSidebarTouchStart(ev: TouchEvent): void {
    const el = ev.currentTarget as HTMLElement;
    // Only start tracking when the user begins the gesture from the very top.
    if (!el || el.scrollTop > 0 || this.ptrRefreshing) { this.ptrEnabled = false; return; }
    this.ptrStartY = ev.touches[0]?.clientY ?? 0;
    this.ptrEnabled = true;
  }
  onSidebarTouchMove(ev: TouchEvent): void {
    if (!this.ptrEnabled || this.ptrRefreshing) return;
    const y = ev.touches[0]?.clientY ?? this.ptrStartY;
    const raw = Math.max(0, y - this.ptrStartY);
    // Rubber-band: ease the distance so it feels native (max ~100px).
    this.ptrPullDist = Math.min(100, raw * 0.55);
    // If pulling visibly down, prevent the page from scrolling instead.
    if (this.ptrPullDist > 4 && ev.cancelable) ev.preventDefault();
  }
  onSidebarTouchEnd(): void {
    if (!this.ptrEnabled) return;
    if (this.ptrPullDist >= 60 && !this.ptrRefreshing) {
      this.ptrRefreshing = true;
      this.ptrPullDist = 50;  // hold the spinner visible while refreshing
      Promise.resolve(this.state.loadSessions(this.employeeName))
        .finally(() => {
          // Smooth retraction
          setTimeout(() => {
            this.ptrRefreshing = false;
            this.ptrPullDist = 0;
          }, 350);
        });
    } else {
      this.ptrPullDist = 0;  // snap back
    }
    this.ptrEnabled = false;
  }

  stop(): void {
    this.state.stopStreaming();
  }

  selectSuggestion(text: string): void {
    this.inputText = text;
    this.send();
  }

  toggleSteps(idx: number): void {
    this.state.toggleStepsOpen(idx);
  }

  // ── Voice ──────────────────────────────────────────────────────
  /** True while we're waiting on the Groq transcript response. */
  isTranscribing = false;
  voiceLang: string = localStorage.getItem('shajiVoiceLang') || 'multi';
  langPickerOpen = false;
  readonly voiceLangs = [
    { code: 'multi', label: 'English + Hindi', flag: '🌐' },
    { code: 'ar', label: 'Arabic', flag: '🇸🇦' },
    { code: 'en', label: 'English only', flag: '🇬🇧' },
    { code: 'hi', label: 'Hindi only', flag: '🇮🇳' }
  ];
  get voiceLangLabel(): string {
    return this.voiceLangs.find(l => l.code === this.voiceLang)?.label || 'English + Hindi';
  }
  get voiceLangFlag(): string {
    return this.voiceLangs.find(l => l.code === this.voiceLang)?.flag || '🌐';
  }
  selectVoiceLang(code: string): void {
    this.voiceLang = code;
    localStorage.setItem('shajiVoiceLang', code);
    this.langPickerOpen = false;
  }

  /** Cancel recording without uploading or sending. */
  closeVoicePopup(): void {
    try { this.voice.stopListening(); } catch { }
    this.liveTranscript = '';
    this.productVoiceActive = false;
    this.isTranscribing = false;
  }

  async toggleListening(): Promise<void> {
    if (!this.voiceSupported) return;
    if (this.isListening || this.isTranscribing) { await this.confirmVoice(); return; }
    this.productVoiceActive = false;
    this.liveTranscript = '';
    await this.voice.startListening({ userName: this.employeeName, userType: this.userType, language: this.voiceLang });
  }

  async startProductVoice(): Promise<void> {
    if (!this.voiceSupported) return;
    if (this.isListening || this.isTranscribing) { await this.confirmVoice(); return; }
    this.productVoiceActive = true;
    this.liveTranscript = '';
    await this.voice.startListening({ userName: this.employeeName, userType: this.userType, language: this.voiceLang });
  }

  /** Stop the live stream, get the final transcript, fill composer, send. */
  async confirmVoice(): Promise<void> {
    if (this.isTranscribing) return;
    this.isTranscribing = true;
    this.voice.setTranscribingState(true);
    const text = (await this.voice.stopRecording() || '').trim();
    this.isTranscribing = false;
    this.voice.setTranscribingState(false);
    if (!text) {
      this.liveTranscript = '(Could not catch that — try again)';
      this.voice.setTranscript(this.liveTranscript);
      setTimeout(() => { this.voice.hidePopup(); this.productVoiceActive = false; }, 1200);
      return;
    }
    this.liveTranscript = text;
    this.voice.setTranscript(text);
    setTimeout(() => {
      this.inputText = this.productVoiceActive ? ('Look up product: ' + text) : text;
      this.voice.hidePopup();
      this.productVoiceActive = false;
      setTimeout(() => this.autoResizeRef(), 0);
      if (!this.isLoading && !this.isStreaming) this.send();
    }, 250);
  }

  /** Tap-to-drill options menu (popover) state. */
  drillMenu: { visible: boolean; x: number; y: number; value: string; options: { label: string; prompt: string }[] } =
    { visible: false, x: 0, y: 0, value: '', options: [] };

  /** Run a chosen drill option — send its prompt as a fresh question. */
  runDrill(opt: { label: string; prompt: string }): void {
    this.drillMenu.visible = false;
    if (!opt?.prompt) return;
    this.inputText = opt.prompt;
    setTimeout(() => this.autoResizeRef(), 0);
    if (!this.isLoading && !this.isStreaming) this.send();
  }
  closeDrill(): void { this.drillMenu.visible = false; }

  /** Click delegation on an AI bubble — drives drill-down on currency spans + action buttons. */
  onMessageClick(e: MouseEvent, msg: any): void {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.classList && target.classList.contains('ai-drill')) {
      e.stopPropagation();
      const value = target.getAttribute('data-drill') || target.textContent || '';
      if (!value) return;
      const type = target.getAttribute('data-drill-type') || 'amount';
      const options = this.state.drillOptions(type, value, msg?.text || '');
      if (!options.length) return;
      const MW = 230, MH = Math.min(options.length * 46 + 38, 280);
      const x = Math.max(8, Math.min(e.clientX, window.innerWidth - MW - 8));
      const y = (e.clientY + MH > window.innerHeight - 8) ? Math.max(8, e.clientY - MH) : e.clientY + 6;
      this.drillMenu = { visible: true, x, y, value, options };
      return;
    }
    if (target.classList && target.classList.contains('ai-action')) {
      e.stopPropagation();
      const label = target.getAttribute('data-action') || target.textContent || '';
      if (!label) return;
      this.inputText = label;
      setTimeout(() => this.autoResizeRef(), 0);
      if (!this.isLoading && !this.isStreaming) this.send();
    }
  }

  /** Speak (or stop) the given AI message text */
  toggleSpeak(messageText: string, index: number): void {
    if (!this.speechSupported) return;
    if (this.isSpeaking && this.currentSpeakingIndex === index) {
      this.voice.stopSpeaking();
      this.currentSpeakingIndex = null;
      return;
    }
    const clean = this.stripMarkup(messageText || '');
    if (!clean) return;
    this.currentSpeakingIndex = index;
    this.voice.speak(clean, { onEnd: () => this.currentSpeakingIndex = null });
  }

  /** Strip markdown, HTML, table pipes, code backticks, and emoji ranges so TTS sounds natural */
  stripMarkup(s: string): string {
    if (!s) return '';
    let out = s;
    out = out.replace(/<[^>]+>/g, ' ');
    out = out.replace(/```[\s\S]*?```/g, ' ');
    out = out.replace(/`([^`]*)`/g, '$1');
    out = out.replace(/\*\*([^*]+)\*\*/g, '$1');
    out = out.replace(/\*([^*]+)\*/g, '$1');
    out = out.replace(/__([^_]+)__/g, '$1');
    out = out.replace(/_([^_]+)_/g, '$1');
    out = out.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    out = out.replace(/^\s{0,3}#{1,6}\s+/gm, '');
    out = out.replace(/^\s*[-*+]\s+/gm, '');
    out = out.replace(/^\s*\d+\.\s+/gm, '');
    out = out.replace(/\|/g, ' ');
    out = out.replace(/^[\s:-]+$/gm, ' ');
    out = out.replace(/[\u{1F300}-\u{1FAFF}]/gu, ' ');
    out = out.replace(/[\u{2600}-\u{27BF}]/gu, ' ');
    out = out.replace(/[\u{1F000}-\u{1F2FF}]/gu, ' ');
    out = out.replace(/[\u{FE0F}\u{200D}]/gu, '');
    out = out.replace(/\s+/g, ' ').trim();
    return out;
  }

  // ── Chart type switcher ────────────────────────────────────────
  /**
   * Returns the interchangeable chart types the user can switch to.
   * Same set offered regardless of series count — ECharts handles
   * single-series gracefully for stacked/radar.
   */
  availableChartTypes(_currentType?: string): Array<{ id: string; label: string; icon: string }> {
    const labels: Record<string, [string, string]> = {
      bar: ['Bar', '▮'],
      line: ['Line', '╱'],
      area: ['Area', '◢'],
      horizontalBar: ['H-Bar', '▬'],
      pie: ['Pie', '◔'],
      donut: ['Donut', '◍'],
      stackedBar: ['Stacked', '▤'],
      scatter: ['Scatter', '⋮⋮'],
      radar: ['Radar', '✦']
    };
    return ['bar', 'line', 'area', 'horizontalBar', 'pie', 'donut', 'stackedBar', 'scatter', 'radar']
      .map(id => ({ id, label: labels[id][0], icon: labels[id][1] }));
  }

  /** Swap the chart type for an existing AI message and rebuild ECharts options. */
  switchChartType(msgIndex: number, newType: string): void {
    const msg = this.messages[msgIndex];
    if (!msg?.chartData) return;
    const newData = { ...msg.chartData, type: newType as any };
    const newOpts = this.state.buildChartOptions(newData);
    // Use the public patchMessage method exposed on the state service.
    this.state.patchMessage(msgIndex, { chartData: newData, chartOptions: newOpts });
    setTimeout(() => window.dispatchEvent(new Event('resize')), 80);
  }

  /** Number of chips hidden when the strip is collapsed (used by "+N more" pill) */
  get hiddenChipsCount(): number {
    return Math.max(0, this.suggestions.length - this.CHIP_VISIBLE_LIMIT);
  }

  /** Chips actually rendered — first N when collapsed, all when expanded */
  get visibleChips(): string[] {
    if (this.chipsExpanded) return this.suggestions;
    return this.suggestions.slice(0, this.CHIP_VISIBLE_LIMIT);
  }

  toggleChipsExpanded(): void {
    this.chipsExpanded = !this.chipsExpanded;
    this.shouldScroll = true;
  }

  // Clicking Report opens a popup asking what the issue is (unless the chat is empty).
  openReportPanel(): void {
    if (this.isReporting) return;
    if (!this.canReport) {
      this.reportToast = { type: 'error', text: 'Start a conversation first — there\'s nothing to report yet.' };
      setTimeout(() => { this.reportToast = null; }, 3500);
      return;
    }
    this.reportText = '';
    this.reportModalOpen = true;
  }

  cancelReport(): void {
    this.reportModalOpen = false;
    this.reportText = '';
  }

  // Capture the screen + send the report WITH the user's description.
  async submitReport(): Promise<void> {
    if (this.isReporting) return;
    const desc = this.reportText.trim();
    if (!desc) return;                       // description is required
    this.reportModalOpen = false;
    this.isReporting = true;
    this.reportToast = { type: 'success', text: 'Capturing screen…' };
    try {
      const cap = await this.screenCapture.capture();
      this.reportToast = { type: 'success', text: 'Sending report…' };
      await this.service.reportIssue(
        this.currentSessionId,
        this.employeeName,
        this.messages,
        cap.imageBase64 || undefined,
        cap.context,
        desc
      ).toPromise();
      this.reportToast = { type: 'success', text: 'Report sent. Thank you!' };
      this.reportText = '';
    } catch (e) {
      console.error('[Report] failed', e);
      this.reportToast = { type: 'error', text: 'Could not send report. Try again later.' };
    } finally {
      this.isReporting = false;
      setTimeout(() => { this.reportToast = null; }, 4000);
    }
  }

  // F.3: Export the current conversation as a PDF. We render a clean printable
  // HTML doc (reusing the same markdown/table formatter the chat uses) into a new
  // window and trigger print — the user picks "Save as PDF". Dependency-free and
  // works in mobile browsers / Android WebView. Falls back to a .txt download if
  // the popup is blocked.
  exportChat(): void {
    const esc = (s: string) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // Strip interactive [action:...] markup — meaningless on paper.
    const clean = (t: string) => (t || '').replace(/\[action:[^\]]+\]/gi, '').trim();
    const title = this.currentTitle || 'Shaji AI chat';
    const when = new Date().toLocaleString();
    const dateTag = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');

    const rows = this.messages.map(m => {
      const who = m.role === 'ai' ? 'Shaji AI' : (this.employeeName || 'You');
      const cls = m.role === 'ai' ? 'ai' : 'user';
      const bodyHtml = this.state.formatMessage(clean(m.text)) || esc(m.text);
      return `<div class="msg ${cls}"><div class="who">${esc(who)} <span class="t">${esc(m.time || '')}</span></div><div class="body">${bodyHtml}</div></div>`;
    }).join('');

    const doc = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
*{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1a1a1a;margin:0;padding:28px;}
.hdr{border-bottom:3px solid #16a34a;padding-bottom:12px;margin-bottom:20px}
.hdr h1{font-size:18px;margin:0 0 4px;color:#16a34a}
.hdr .meta{font-size:12px;color:#666}
.msg{margin:0 0 16px;page-break-inside:avoid}
.who{font-size:12px;font-weight:600;color:#16a34a;margin-bottom:3px}
.msg.user .who{color:#444}
.who .t{font-weight:400;color:#999;margin-left:6px;font-size:11px}
.body{font-size:13px;line-height:1.5}
.body table{border-collapse:collapse;width:100%;margin:8px 0;font-size:12px}
.body th,.body td{border:1px solid #ddd;padding:5px 8px;text-align:left}
.body th{background:#f3f6f3}
.body pre{background:#f5f5f5;padding:8px;border-radius:6px;overflow:auto;font-size:12px}
.ai-action,.chip,.chip-more{display:none !important}
@media print{body{padding:0 12px}}
</style></head><body>
<div class="hdr"><h1>${esc(title)}</h1><div class="meta">Shaji Paints AI · ${esc(this.employeeName || '')} · ${esc(when)}</div></div>
${rows}
<script>window.onload=function(){setTimeout(function(){window.print();},300);};<\/script>
</body></html>`;

    const w = window.open('', '_blank');
    if (!w) {
      // Popup blocked → fall back to plain-text download.
      const txt = `Shaji Paints AI — ${title}\n${when}\n${'='.repeat(50)}\n\n` +
        this.messages.map(m => `${m.role === 'ai' ? 'Shaji AI' : this.employeeName}:\n${clean(m.text)}`).join('\n\n');
      const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `shaji-ai-chat-${dateTag}.txt`; a.click();
      URL.revokeObjectURL(url);
      return;
    }
    w.document.open();
    w.document.write(doc);
    w.document.close();
  }

  // ── Input handlers ─────────────────────────────────────────────
  onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!this.isLoading && !this.isStreaming) this.send();
    }
  }

  autoResize(e: Event): void {
    const el = e.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 220) + 'px';
  }

  private autoResizeRef(): void {
    const el = this.composerEl?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 220) + 'px';
  }

  // ⌘N / Ctrl+N → new chat
  @HostListener('window:keydown', ['$event'])
  onGlobalKey(e: KeyboardEvent): void {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n') {
      e.preventDefault();
      this.newChat();
    }
  }

  // ── Search filter (F.1) ────────────────────────────────────────
  // Instant local filter on titles + a debounced backend call that ALSO searches
  // inside message content (sessions that match by content but not title show up
  // under "Found in messages" with a snippet).
  onSearch(): void {
    this.applySearchFilter();
    const q = this.searchQuery.trim();
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    if (q.length < 2) { this.contentMatches = []; return; }
    this.searchDebounce = setTimeout(() => {
      this.service.searchAiSessions(q).subscribe({
        next: (r: any) => {
          const localIds = new Set(this.filteredGroups.flatMap(g => g.sessions.map(s => s.session_id)));
          // Only show content hits not already covered by the title filter.
          this.contentMatches = (r?.results || []).filter((m: any) => m.snippet && !localIds.has(m.session_id));
        },
        error: () => { this.contentMatches = []; }
      });
    }, 280);
  }
  private applySearchFilter(): void {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) { this.filteredGroups = this.sessionGroups; this.contentMatches = []; return; }
    this.filteredGroups = this.sessionGroups
      .map(g => ({ ...g, sessions: g.sessions.filter(s => s.title.toLowerCase().includes(q)) }))
      .filter(g => g.sessions.length > 0);
  }
  // Open a content-search hit by session id.
  openContentMatch(m: { session_id: string; title: string }): void {
    this.mobileSidebarOpen = false;
    this.state.openSession({ session_id: m.session_id, title: m.title } as ChatSession, this.employeeName);
    this.searchQuery = '';
    this.contentMatches = [];
    this.applySearchFilter();
  }

  // ── Favorites (F.2) ────────────────────────────────────────────
  loadFavorites(): void {
    this.service.getFavorites().subscribe({
      next: (r: any) => { this.favorites = r?.favorites || []; },
      error: () => { this.favorites = []; }
    });
  }
  // Save the current composer text (or the last thing the user asked) as a favorite.
  saveFavorite(): void {
    let text = this.inputText.trim();
    if (!text) {
      const lastUser = [...this.messages].reverse().find(m => m.role === 'user');
      text = (lastUser?.text || '').trim();
    }
    if (!text) return;
    const label = text.length > 40 ? text.slice(0, 40).trim() + '…' : text;
    this.service.addFavorite(label, text).subscribe({
      next: (r: any) => {
        if (r?.favorite && !this.favorites.some(f => f.id === r.favorite.id)) {
          this.favorites = [r.favorite, ...this.favorites];
        }
      },
      error: () => { }
    });
  }
  get canSaveFavorite(): boolean {
    const text = this.inputText.trim() || [...this.messages].reverse().find(m => m.role === 'user')?.text?.trim() || '';
    return !!text && !this.favorites.some(f => f.query_text === text);
  }
  runFavorite(f: { query_text: string }): void {
    if (this.isLoading || this.isStreaming) return;
    this.mobileSidebarOpen = false;
    this.state.sendMessage(f.query_text, this.userType, this.employeeName);
  }
  removeFavorite(f: { id: string }, e: Event): void {
    e.stopPropagation();
    this.favorites = this.favorites.filter(x => x.id !== f.id);
    this.service.deleteFavorite(f.id).subscribe({ next: () => { }, error: () => { } });
  }

  // ── Helpers ────────────────────────────────────────────────────
  trackByIndex(_: number, __: any): any { return _; }

  private scrollToBottom(): void {
    try {
      const el = this.threadEl?.nativeElement;
      if (!el) return;
      if (this.atBottom) {
        el.scrollTop = el.scrollHeight;     // follow the stream
        this.showJumpBtn = false;
      } else {
        this.showJumpBtn = true;            // user scrolled up — offer a pill instead of yanking
      }
    } catch { }
  }
}
